import { useCallback, useEffect } from 'react';
import { AudioLines } from 'lucide-react';
import type { ProjectTask } from '@/types/task';
import { useMediaPlayer } from '../hooks/useMediaPlayer';
import { MediaControls } from './MediaControls';

interface AudioPlayerProps {
  resource: {
    id: string;
    signedUrl?: string;
    mimeType?: string;
    [k: string]: unknown;
  };
  tasks: ProjectTask[];
  selectedTask?: ProjectTask | null;
  updateSelectedTask: (task: ProjectTask | null) => void;
  messagesTabOpen: boolean;
  userRole: string | null;
  /** Called when user clicks the waveform area to create a task */
  onCreateTask?: (time: number) => void;
}

export function AudioPlayer({
  resource,
  tasks,
  selectedTask,
  updateSelectedTask,
  messagesTabOpen,
  userRole,
  onCreateTask,
}: AudioPlayerProps) {
  const player = useMediaPlayer();
  const tasksVisible = !messagesTabOpen;
  const isViewer = userRole === 'viewer' || userRole === 'guest';

  // Reload media when resource changes
  useEffect(() => {
    player.load();
  }, [resource.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContentClick = useCallback(() => {
    if (tasksVisible && !isViewer && onCreateTask) {
      player.pause();
      onCreateTask((player.ref.current?.currentTime ?? 0) * 1000);
    } else {
      player.togglePlay();
    }
  }, [tasksVisible, isViewer, onCreateTask, player]);

  return (
    <div className="group/player relative flex size-full flex-col rounded-lg overflow-hidden">
      {/* Hidden audio element */}
      <audio
        ref={player.ref as React.RefObject<HTMLAudioElement>}
        preload="auto"
        crossOrigin="anonymous"
      >
        {resource.signedUrl && (
          <source src={resource.signedUrl} type={resource.mimeType} />
        )}
      </audio>

      {/* Waveform visual area */}
      <div
        className="flex flex-1 cursor-pointer items-center justify-center bg-blue-600 rounded-t-lg"
        onClick={handleContentClick}
      >
        <AudioLines
          className={`size-1/3 max-h-32 text-white transition-transform ${
            player.isPlaying ? 'animate-pulse' : ''
          }`}
        />
      </div>

      {/* Controls at bottom */}
      <div className="relative h-14 bg-gray-900 rounded-b-lg">
        <div className="absolute inset-0 flex flex-col justify-center opacity-100">
          <MediaControls
            currentTime={player.currentTime}
            duration={player.duration}
            isPlaying={player.isPlaying}
            isFullscreen={false}
            playbackRate={player.playbackRate}
            onTogglePlay={player.togglePlay}
            onSeek={player.seek}
            onSetRate={player.setRate}
            onToggleFullscreen={() => {}}
            tasks={tasksVisible ? tasks : undefined}
            selectedTaskId={selectedTask?.id}
            onSelectTask={(t) => {
              player.seek((t.timestamp ?? 0) / 1000);
              player.pause();
              updateSelectedTask(t as ProjectTask);
            }}
            hideFullscreen
            mediaRef={player.ref}
          />
        </div>
      </div>
    </div>
  );
}
